import { createContext, useContext, useEffect, useState } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading]         = useState(true);

  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function logout() {
    setUserProfile(null);
    return signOut(auth);
  }

  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  async function updateUserProfile(data) {
    if (!currentUser) return;
    const userRef = doc(db, "users", currentUser.uid);
    await setDoc(userRef, { ...data, updatedAt: serverTimestamp() }, { merge: true });
    const updated = await getDoc(userRef);
    if (updated.exists()) setUserProfile(normalizeProfile(updated.data()));
  }

  // Handles both "Role"/"role", "Email"/"email" etc from Firestore
  function normalizeProfile(data) {
    if (!data) return null;
    const role = (data.role || data.Role || "member").toString().toLowerCase().trim();
    return {
      ...data,
      role,
      email:  data.email  || data.Email  || "",
      name:   data.name   || data.Name   || "",
      status: (data.status || data.Status || "active").toLowerCase().trim(),
    };
  }

  async function fetchUserProfile(uid) {
    try {
      const userRef = doc(db, "users", uid);
      const snap    = await getDoc(userRef);
      if (snap.exists()) {
        const normalized = normalizeProfile(snap.data());
        console.log("✅ Profile loaded:", normalized);
        setUserProfile(normalized);
        return normalized;
      } else {
        console.log("❌ No Firestore profile found for UID:", uid);
        setUserProfile(null);
        return null;
      }
    } catch (err) {
      console.error("🔥 Firestore fetch error:", err);
      return null;
    }
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const value = {
    currentUser,
    userProfile,
    loading,
    login,
    logout,
    resetPassword,
    updateUserProfile,
    fetchUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}